import { useEffect, useState } from 'react';
import { Alert, Box, Button, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useLoginMutation, useSetupShopMutation } from '../api/auth/AuthApi';
import { signIn as storeUser } from '../utils/auth';

const SLIDE = 'all .55s ease-in-out';

/**
 * The retail suite's own login background, copied from its `style.css` so the two
 * products open the same way - two striped gradients laid over each other, the
 * pastel one softening the purple underneath.
 */
const PAGE_BACKGROUND =
  'linear-gradient(45deg,' +
  ' rgba(254,246,210,.53) 0%, rgba(254,246,210,.53) 14.286%,' +
  ' rgba(221,240,216,.53) 14.286%, rgba(221,240,216,.53) 28.572%,' +
  ' rgba(188,233,223,.53) 28.572%, rgba(188,233,223,.53) 42.858%,' +
  ' rgba(156,227,229,.53) 42.858%, rgba(156,227,229,.53) 57.144%,' +
  ' rgba(123,220,235,.53) 57.144%, rgba(123,220,235,.53) 71.43%,' +
  ' rgba(90,214,242,.53) 71.43%, rgba(90,214,242,.53) 85.716%,' +
  ' rgba(57,207,248,.53) 85.716%, rgba(57,207,248,.53) 100%),' +
  'linear-gradient(135deg,' +
  ' rgb(246,99,200) 0%, rgb(246,99,200) 12.5%, rgb(223,98,196) 12.5%, rgb(223,98,196) 25%,' +
  ' rgb(199,97,192) 25%, rgb(199,97,192) 37.5%, rgb(176,96,188) 37.5%, rgb(176,96,188) 50%,' +
  ' rgb(152,95,184) 50%, rgb(152,95,184) 62.5%, rgb(129,94,180) 62.5%, rgb(129,94,180) 75%,' +
  ' rgb(105,93,176) 75%, rgb(105,93,176) 87.5%, rgb(82,92,172) 87.5%, rgb(82,92,172) 100%)';

/** The same panel gradient the retail suite slides across. */
const OVERLAY = 'linear-gradient(45deg, #b060bcba, #23a6d5c2)';

/** What the shop gets, in three lines. Shown on the brand half. */
const SELLING_POINTS = [
  'Every piece tracked by serial number',
  'Batch-wise cost, so profit is real',
  'Cash, UPI, credit and EMI in one bill',
];

const ghostButton = {
  borderRadius: 999,
  borderColor: 'rgba(255,255,255,.7)',
  color: '#fff',
  fontWeight: 700,
  letterSpacing: '.06em',
  px: 4.5,
  py: 0.9,
  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,.14)' },
};

/** A phone, drawn in CSS. No image to load, and it scales with the panel. */
const PhoneMark = () => (
  <Box
    aria-hidden
    sx={{
      width: 62,
      height: 104,
      borderRadius: 2.5,
      border: '2.5px solid rgba(255,255,255,.55)',
      position: 'relative',
      mb: 3,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 7,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 20,
        height: 3,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,.55)',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 22,
        height: 22,
        borderRadius: '50%',
        border: '2px solid rgba(255,255,255,.35)',
      },
    }}
  />
);

/**
 * Sign in and sign up on one page.
 *
 * <p>A split card: the shop's own colour on one side carrying its name, plain white
 * on the other carrying the form. The coloured half slides across to swap between
 * signing in and signing up, which is the same shape the retail suite opens with.
 *
 * <p>The shop's name comes from `/auth/status`, which answers before anybody has
 * signed in: staff should see at a glance that this is their shop's machine, and the
 * name is printed on every bill anyway.
 *
 * <p>Signing up is how a shop starts - it names itself and creates its owner, once.
 * After that the panel says so rather than pretending, because further logins are
 * made by the owner under Settings, where they can be given a role.
 */
const AuthPage = ({
  setupNeeded,
  shopName,
  onSignedIn,
}: {
  setupNeeded: boolean;
  shopName?: string;
  onSignedIn: () => void;
}) => {
  const [showSignIn, setShowSignIn] = useState(!setupNeeded);
  const [error, setError] = useState('');

  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [newShopName, setNewShopName] = useState('');
  const [fullName, setFullName] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  // Each box is revealed on its own, so showing the first to check a typo does not
  // defeat the point of typing the second.
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirm, setConfirm] = useState('');

  const [login, { isLoading: signingIn }] = useLoginMutation();
  const [setupShop, { isLoading: signingUp }] = useSetupShopMutation();

  // A brand-new shop lands on Sign Up; there is nothing to sign in to yet.
  useEffect(() => {
    setShowSignIn(!setupNeeded);
  }, [setupNeeded]);

  const title = shopName?.trim() || 'Mobile Shop';

  const submitSignIn = async () => {
    if (!userName.trim() || !password) {
      setError('Enter your user name and password');
      return;
    }
    try {
      const result = await login({ userName: userName.trim(), password }).unwrap();
      storeUser({
        token: result.token,
        userName: result.userName,
        fullName: result.fullName,
        role: result.role,
        roleName: result.roleName,
        // What this person may do, decided by the owner's permission grid.
        permissions: result.permissions,
      });
      onSignedIn();
    } catch (err: any) {
      setError(err?.data?.message || 'Could not sign in');
    }
  };

  const submitSignUp = async () => {
    if (!setupNeeded) {
      setError('This shop is already set up. Ask the owner to create your login.');
      return;
    }
    if (!newShopName.trim() || !fullName.trim() || !newUserName.trim() || !newPassword) {
      setError('Fill in every box');
      return;
    }
    if (newPassword.length < 4) {
      setError('Choose a password of at least 4 characters');
      return;
    }
    if (newPassword !== confirm) {
      setError('The two passwords do not match');
      return;
    }
    try {
      const result = await setupShop({
        shopName: newShopName.trim(),
        fullName: fullName.trim(),
        userName: newUserName.trim(),
        password: newPassword,
      }).unwrap();
      storeUser({
        token: result.token,
        userName: result.userName,
        fullName: result.fullName,
        role: result.role,
        roleName: result.roleName,
        permissions: result.permissions,
      });
      onSignedIn();
    } catch (err: any) {
      setError(err?.data?.message || 'Could not create the shop');
    }
  };

  const switchTo = (toSignIn: boolean) => {
    setShowSignIn(toSignIn);
    setError('');
  };

  /** Each form sits in its own half and slides sideways when the panel moves. */
  const formHalf = (visible: boolean, zIndex: number) => ({
    position: { xs: 'static', md: 'absolute' } as const,
    top: 0,
    left: 0,
    height: { xs: 'auto', md: '100%' },
    width: { xs: '100%', md: '50%' },
    transition: SLIDE,
    display: { xs: visible ? 'flex' : 'none', md: 'flex' },
    flexDirection: 'column',
    justifyContent: 'center',
    px: { xs: 3, md: 6 },
    py: { xs: 4, md: 0 },
    bgcolor: 'background.paper',
    zIndex,
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 2,
        background: PAGE_BACKGROUND,
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 960,
          minHeight: { xs: 'auto', md: 560 },
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 14px 28px rgba(0,0,0,.25), 0 10px 10px rgba(0,0,0,.22)',
        }}
      >
        <Box sx={{ position: 'relative', minHeight: { xs: 'auto', md: 560 } }}>
          {/* ---------------------------------------------------------- sign up */}
          <Box
            sx={{
              ...formHalf(!showSignIn, showSignIn ? 1 : 5),
              opacity: { xs: 1, md: showSignIn ? 0 : 1 },
              transform: { xs: 'none', md: showSignIn ? 'none' : 'translateX(100%)' },
            }}
            component="form"
            onSubmit={(event: React.FormEvent) => {
              event.preventDefault();
              submitSignUp();
            }}
          >
            <Typography variant="h5" fontWeight={700}>
              {setupNeeded ? 'Create your shop' : 'Sign Up'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
              {setupNeeded
                ? 'Name the shop and choose your own password. This happens once.'
                : 'This shop is already set up. The owner creates new logins under Settings > Users.'}
            </Typography>

            <Stack gap={2}>
              {!showSignIn && error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="Shop Name"
                size="small"
                disabled={!setupNeeded}
                value={newShopName}
                onChange={(event) => {
                  setNewShopName(event.target.value);
                  setError('');
                }}
              />
              <TextField
                label="Your Name"
                size="small"
                disabled={!setupNeeded}
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
              />
              <TextField
                label="User Name"
                size="small"
                autoComplete="username"
                disabled={!setupNeeded}
                value={newUserName}
                onChange={(event) => setNewUserName(event.target.value)}
              />
              {/* Both boxes can be read back, as the sign-in one can. This is the
                  password the shop will use from now on and is typed twice from
                  memory - being unable to see either of them is how somebody ends up
                  locked out of a shop they set up five minutes ago. */}
              <Stack direction="row" gap={2}>
                <TextField
                  label="Password"
                  type={showNewPassword ? 'text' : 'password'}
                  size="small"
                  fullWidth
                  autoComplete="new-password"
                  disabled={!setupNeeded}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          disabled={!setupNeeded}
                          onClick={() => setShowNewPassword((shown) => !shown)}
                          aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                        >
                          {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Confirm"
                  type={showConfirm ? 'text' : 'password'}
                  size="small"
                  fullWidth
                  autoComplete="new-password"
                  disabled={!setupNeeded}
                  value={confirm}
                  // Says so on the box rather than only after Create is pressed.
                  error={confirm.length > 0 && confirm !== newPassword}
                  helperText={confirm.length > 0 && confirm !== newPassword ? 'The two do not match' : ' '}
                  onChange={(event) => setConfirm(event.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          disabled={!setupNeeded}
                          onClick={() => setShowConfirm((shown) => !shown)}
                          aria-label={showConfirm ? 'Hide password' : 'Show password'}
                        >
                          {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              <LoadingButton
                type="submit"
                variant="contained"
                size="large"
                loading={signingUp}
                disabled={!setupNeeded}
                sx={{ borderRadius: 999, mt: 0.5, py: 1.1, fontWeight: 700, letterSpacing: '.05em' }}
              >
                Sign Up
              </LoadingButton>
              <Button size="small" color="inherit" onClick={() => switchTo(true)} sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
                Already have a login? Sign in
              </Button>
            </Stack>
          </Box>

          {/* ---------------------------------------------------------- sign in */}
          <Box
            sx={{
              ...formHalf(showSignIn, 2),
              transform: { xs: 'none', md: showSignIn ? 'none' : 'translateX(100%)' },
            }}
            component="form"
            onSubmit={(event: React.FormEvent) => {
              event.preventDefault();
              submitSignIn();
            }}
          >
            <Typography variant="h5" fontWeight={700}>
              Sign in
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
              Sign in to start billing.
            </Typography>

            <Stack gap={2.5}>
              {showSignIn && error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="User Name"
                autoFocus
                autoComplete="username"
                value={userName}
                onChange={(event) => {
                  setUserName(event.target.value);
                  setError('');
                }}
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setError('');
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword((shown) => !shown)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <LoadingButton
                type="submit"
                variant="contained"
                size="large"
                loading={signingIn}
                sx={{ borderRadius: 999, py: 1.1, fontWeight: 700, letterSpacing: '.05em' }}
              >
                Sign In
              </LoadingButton>
              <Button size="small" color="inherit" onClick={() => switchTo(false)} sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
                New shop? Sign up
              </Button>
            </Stack>
          </Box>

          {/* ------------------------------------------- the half that slides */}
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              top: 0,
              left: '50%',
              width: '50%',
              height: '100%',
              overflow: 'hidden',
              transition: 'transform .55s ease-in-out',
              transform: showSignIn ? 'none' : 'translateX(-100%)',
              zIndex: 100,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                left: '-100%',
                height: '100%',
                width: '200%',
                color: '#fff',
                background: OVERLAY,
                transition: 'transform .55s ease-in-out',
                transform: showSignIn ? 'translateX(0)' : 'translateX(50%)',
              }}
            >
              {/* Shown while signing up: the way back to sign in. */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  height: '100%',
                  width: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 5,
                  transition: 'transform .55s ease-in-out',
                  transform: showSignIn ? 'translateX(-20%)' : 'translateX(0)',
                }}
              >
                <PhoneMark />
                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '.06em' }}>
                  {title.toUpperCase()}
                </Typography>
                <Box sx={{ width: 44, height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,.7)', my: 2 }} />
                <Typography sx={{ opacity: 0.92, lineHeight: 1.8, mb: 3 }}>
                  Already have a login? Sign in and start billing.
                </Typography>
                <Button variant="outlined" onClick={() => switchTo(true)} sx={ghostButton}>
                  Sign In
                </Button>
              </Box>

              {/* Shown while signing in: the shop, and the way over to sign up. */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  height: '100%',
                  width: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 5,
                  transition: 'transform .55s ease-in-out',
                  transform: showSignIn ? 'translateX(0)' : 'translateX(20%)',
                }}
              >
                <PhoneMark />
                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '.06em' }}>
                  {title.toUpperCase()}
                </Typography>
                <Box sx={{ width: 44, height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,.7)', my: 2 }} />

                <Stack gap={1.2} sx={{ mb: 3 }}>
                  {SELLING_POINTS.map((point) => (
                    <Stack key={point} direction="row" alignItems="center" gap={1}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 18, opacity: 0.85 }} />
                      <Typography variant="body2" sx={{ opacity: 0.92 }}>
                        {point}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                {/* Always offered, even once the shop is set up: the panel then
                    explains that logins are made by the owner, which is a better
                    answer than a button that is not there. */}
                <Button variant="outlined" onClick={() => switchTo(false)} sx={ghostButton}>
                  Sign Up
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.9)', textShadow: '0 1px 2px rgba(0,0,0,.25)' }}>
        {title} &middot; {new Date().getFullYear()}
      </Typography>
    </Box>
  );
};

export default AuthPage;
